import { Navigate } from 'react-router-dom'
import { useStoreOwner } from '../store/useStoreOwner'
import { useStaffAuth } from '../store/useStaffAuth'

/**
 * Wraps store-owner pages that require Stripe Connect KYC to be approved.
 * Auth (login) check is still done by RequireStoreAuth — wrap with both.
 *
 * Phase B-1: ownerStatus is mocked client-side via useStoreOwner.setOwnerStatus.
 * Phase B-2 will replace it with a backend fetch from /store-auth/connect/status.
 */
export default function RequireApprovedOwner({ children }: { children: React.ReactNode }) {
  const { isStoreLoggedIn, loading, ownerStatus, ownerStatusLoading, cashOnlyOwner } = useStoreOwner()
  const staffSession = useStaffAuth((s) => s.session)

  // #995 スタッフモード（staff セッションあり）は KYC 承認チェック対象外。
  if (staffSession) return <>{children}</>

  // ownerStatus の初回 fetch が完了する前は redirect を保留する。
  // ownerStatus 初期値 'unset' を「未連携」と区別できないため、ownerStatusLoading
  // が無いと URL bar 直入力時に fetch 完了前の 'unset' を見て /store/onboarding
  // へ誤 redirect する (#840 で /store/preferences が踏んだ race)。
  if (loading || (isStoreLoggedIn && ownerStatusLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
      </div>
    )
  }

  if (!isStoreLoggedIn) {
    return <Navigate to="/store/login" replace />
  }

  // #1431 本人確認をせず「現金のみ」で始めたオーナーは通す。
  //
  // この門はセキュリティ境界ではない（ログイン認証は RequireStoreAuth が別に
  // 見ている）。「準備が終わるまで使わせない」という案内用のリダイレクトで、
  // **置かれた理由は「KYC 未完了だと Stripe が決済を受け付けず、レジを開いても
  // 何も課金できない」から**。現金は当社を一切通らず Stripe も動かないので、
  // その前提が成り立たない。
  //
  // KYC を理由に店舗が入口で止まり、実証実験自体が始まらない方が損失が大きい。
  // まず使ってもらい、あとからカード決済（= 手数料）へ移ってもらう。
  if (ownerStatus !== 'approved' && !cashOnlyOwner) {
    return <Navigate to="/store/onboarding" replace />
  }

  return <>{children}</>
}
